// src/utils/mockDataAgents.ts
// All 13 Independent AI Agents — profiles, mock outputs, knowledge authorities
// Pre-infrastructure mode: agents render mock outputs so the UI is fully demonstrable
// before Neon + Cloudflare are connected.
// When real infrastructure is connected, set AGENT_MOCK_MODE = false.

export const AGENT_MOCK_MODE = false  // live mode — calling Cloudflare Worker

// ── Types ─────────────────────────────────────────────────────────

export type AgentWave   = 1 | 2 | 3 | 4 | 5
export type AgentStatus = 'ACTIVE' | 'PLANNED' | 'INACTIVE'
export type AutonLevel  = 1 | 2 | 3   // 1 Advisory · 2 Assisted · 3 Automated

export interface AgentProfile {
  agentId:          string
  code:             string
  name:             string
  shortName:        string
  aqfLevel:         string
  wave:             AgentWave
  status:           AgentStatus
  autonomyLevel:    AutonLevel
  primaryModules:   string[]
  triggerEvents:    string[]
  knowledgeVersion: string
  knowledgeCurrency:number      // 0–100 score
  nextReviewDate:   string
  primaryKnowledge: string[]
  capabilities:     string
  description:      string
}

export interface AgentAlert {
  id:      string
  title:   string
  detail:  string
  action:  string
  clause?: string
  ref?:    string
}

export interface AgentOutput {
  urgent:   AgentAlert[]
  warning:  AgentAlert[]
  advisory: AgentAlert[]
  summary:  string
}

// ── All 13 agent profiles ─────────────────────────────────────────

export const ALL_AGENTS: AgentProfile[] = [
  {
    agentId: 'A04', code: 'whs-officer', name: 'WHS Officer Agent', shortName: 'WHS Officer',
    aqfLevel: 'AQF 7–8', wave: 1, status: 'ACTIVE', autonomyLevel: 1,
    primaryModules: ['swms','incidents','permits','toolbox','whs-plan','whs-compliance','sds-register'],
    triggerEvents: ['swms_created','swms_updated','incident_created','permit_created'],
    knowledgeVersion: 'WHS-v2.4.1', knowledgeCurrency: 98, nextReviewDate: '2026-09-30',
    primaryKnowledge: [
      'Work Health and Safety Act 2011 (Qld)',
      'WHS Regulation 2011 (Qld) Ch.6 r.291 — HRCW triggers',
      'Code of Practice: Construction Work (Qld) 2018',
      'Code of Practice: Traffic Management for Construction Work',
      'Electrical Safety Act 2002 (Qld)',
    ],
    capabilities: 'SWMS review (r.291 HRCW triggers), incident severity classification, PTW compliance, toolbox talk generation, WorkSafe Qld notification obligations',
    description: 'Reviews safe work method statements for High Risk Construction Work triggers, classifies incident severity, validates permit to work applications, and identifies WorkSafe Queensland notification obligations.',
  },
  {
    agentId: 'A09', code: 'qa-specialist', name: 'QA Specialist Agent', shortName: 'QA Specialist',
    aqfLevel: 'AQF 7–8', wave: 1, status: 'ACTIVE', autonomyLevel: 2,
    primaryModules: ['itp','works-instr','drawings','handover','qa-ncr'],
    triggerEvents: ['itp_hold_point_created','drawing_ifc','work_instruction_completed','handover_initiated'],
    knowledgeVersion: 'QA-v2.1.0', knowledgeCurrency: 96, nextReviewDate: '2026-09-30',
    primaryKnowledge: [
      'MRTS01 Introduction to Specifications (TMR)',
      'MRTS50 Unbound Pavements (TMR)',
      'MRTS11 Drainage (TMR)',
      'MRTS73 Concrete Structures (TMR)',
      'AS/NZS ISO 9001:2016 Quality Management Systems',
    ],
    capabilities: 'ITP generation from MRTS specs, HP/WP/RP classification, NCR management, hold point acceptance criteria, ISO 9001 compliance checking',
    description: 'Generates inspection and test plans from TMR MRTS specifications, classifies hold points, witness points and review points, and identifies non-conformance requirements.',
  },
  {
    agentId: 'A06', code: 'project-manager', name: 'Project Manager Agent', shortName: 'PM Agent',
    aqfLevel: 'AQF 7–8', wave: 2, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['jobs','contracts','claims','reports'],
    triggerEvents: ['claim_period_open','milestone_due'],
    knowledgeVersion: 'PMO-v1.0.0', knowledgeCurrency: 95, nextReviewDate: '2026-10-01',
    primaryKnowledge: [
      'Project Management Body of Knowledge (PMBoK)',
      'AS 4000-1997 General Conditions of Contract',
      'QBCC Act 1991',
    ],
    capabilities: 'Programme management, earned value analysis, payment claim narratives, variation entitlement assessment, delay analysis and EOT',
    description: 'Analyses project schedule performance, generates payment claim narratives, assesses extension of time entitlement, and monitors milestone compliance.',
  },
  {
    agentId: 'A07', code: 'contracts-legal', name: 'Contracts & Legal Agent', shortName: 'Legal Agent',
    aqfLevel: 'AQF 8+', wave: 2, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['contracts','claims','subcontracts'],
    triggerEvents: ['new_contract_uploaded','variation_submitted'],
    knowledgeVersion: 'CL-v1.0.0', knowledgeCurrency: 94, nextReviewDate: '2026-10-01',
    primaryKnowledge: [
      'Building Industry Fairness (Security of Payment) Act 2017 (Qld)',
      'AS 4000-1997 / AS 2124-1992 / NEC4 contract conditions',
      'Queensland Civil and Administrative Tribunal Act 2009',
    ],
    capabilities: 'BCIPA/BIF payment claim compliance, variation entitlement under AS4000/GC21/NEC4, subcontract review, security of payment obligations',
    description: 'Reviews payment claims for BCIPA/BIF compliance, assesses variation entitlement, and identifies security of payment obligations.',
  },
  {
    agentId: 'A13', code: 'contracts-admin', name: 'Contracts Administrator Agent', shortName: 'CA Agent',
    aqfLevel: 'AQF 7–8', wave: 2, status: 'PLANNED', autonomyLevel: 2,
    primaryModules: ['contracts','variations','live-budget','claims','subcontracts','pos','cost-codes'],
    triggerEvents: ['variation_created','variation_notice_critical','variation_approved','po_approved_budget_check','progress_claim_submitted','contract_pc_date_approaching','cost_code_missing_on_claim_line','cost_code_library_pending'],
    knowledgeVersion: 'CA-v2.1.0', knowledgeCurrency: 97, nextReviewDate: '2026-09-30',
    primaryKnowledge: [
      'AS 4000-1997 Cl.41.1 — variation notice time bar (14 calendar days)',
      'AS 2124-1992 Cl.40.1 — variation notice (28 calendar days)',
      'NEC4 Cl.61.3 — compensation event notification (8 weeks)',
      'Building Industry Fairness (Security of Payment) Act 2017 (Qld)',
      'QBCC Act 1991 s.67AA — subcontracting disclosure',
      'TMR PCEM 9th Ed — MRS cost code structure for progress claim line items (Annexure C)',
      'TMR claim line validation — each line must carry MRS group + Work Item ID or claim will be rejected',
    ],
    capabilities: 'Variation register, notice deadline alerting (WARN 7d, URGENT 3d), running contract sum, budget exposure by MRS cost code (80%/90%), PC date and bond expiry, superintendent response clock. ' +
      'Progress claim cost code validation — flags any claim line missing MRS group/work item ID as HIGH RISK before submission. ' +
      'Advises PM to import Estimate Import Template if TMR library is PENDING or ILLUSTRATIVE.',
    description: 'Monitors contractual deadlines, bond expiry, PC dates, and superintendent response obligations. ' +
      'Validates progress claim lines carry correct MRS cost codes before submission — an uncoded claim line will be rejected by TMR. ' +
      'Issues graduated alerts and drafts notices at Level 2.',
  },
  {
    agentId: 'A01', code: 'civil-engineer', name: 'Civil Engineer Agent', shortName: 'Civil Engineer',
    aqfLevel: 'AQF 7', wave: 3, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['estimating','drawings','works-instr','itp'],
    triggerEvents: ['drawing_ifc','boq_line_added'],
    knowledgeVersion: 'CE-v1.0.0', knowledgeCurrency: 93, nextReviewDate: '2027-04-01',
    primaryKnowledge: [
      'Full TMR MRTS specification series',
      'TMR Standard Drawings — civil infrastructure',
      'AustRoads Guide to Road Design',
    ],
    capabilities: 'BOQ review against MRTS specs, ITP hold point classification, work instruction generation, TMR Standard Drawing compliance, EOT narratives',
    description: 'Reviews civil engineering drawings and specifications against TMR MRTS requirements, generates hold point classifications, and validates construction methodology.',
  },
  {
    agentId: 'A02', code: 'electrical-engineer', name: 'Electrical Engineer Agent', shortName: 'Elec. Engineer',
    aqfLevel: 'AQF 7–8', wave: 3, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['drawings','hv-switching','its-comms','itp'],
    triggerEvents: ['hv_switching_order_created','drawing_ifc'],
    knowledgeVersion: 'EE-v1.0.0', knowledgeCurrency: 92, nextReviewDate: '2027-04-01',
    primaryKnowledge: [
      'AS/NZS 3000:2018 Wiring Rules',
      'Energex Network Standards',
      'Ergon Energy Technical Specifications',
    ],
    capabilities: 'HV switching order review, ITS commissioning compliance, AS/NZS 3000 wiring rules, Energex/Ergon standards',
    description: 'Reviews HV switching orders for electrical safety compliance, validates ITS commissioning plans, and checks electrical drawings against wiring rules.',
  },
  {
    agentId: 'A11', code: 'traffic-engineering', name: 'Traffic Engineering Agent', shortName: 'Traffic Eng.',
    aqfLevel: 'AQF 7–8', wave: 3, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['drawings','its-comms','estimating','handover'],
    triggerEvents: ['drawing_ifc','its_commissioning_plan_created'],
    knowledgeVersion: 'TE-v1.0.0', knowledgeCurrency: 91, nextReviewDate: '2027-04-01',
    primaryKnowledge: [
      'AustRoads Guide to Traffic Management',
      'Manual of Uniform Traffic Control Devices (MUTCD)',
      'TMR SCATS/STREAMS operational standards',
    ],
    capabilities: 'Signal phasing validation, SCATS/STREAMS parameters, TMP compliance, AustRoads design guidance',
    description: 'Validates traffic signal phasing plans, reviews traffic management plans for MUTCD compliance, and checks ITS commissioning parameters.',
  },
  {
    agentId: 'A08', code: 'financial', name: 'Financial & Commercial Agent', shortName: 'Financial Agent',
    aqfLevel: 'AQF 8', wave: 4, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['spend-analytics','xero','timesheets','reports','cost-codes'],
    triggerEvents: ['bas_period_close','xero_reconciliation_error','progress_claim_approved_xero_push'],
    knowledgeVersion: 'FC-v1.1.0', knowledgeCurrency: 90, nextReviewDate: '2027-04-01',
    primaryKnowledge: [
      'GST Act 1999 (Cth) — construction industry',
      'Single Touch Payroll Phase 2 (ATO)',
      'AASB 15 Revenue from Contracts with Customers',
      'TMR PCEM Annexure N — CapEx/OpEx classification: ITS/signals/lighting scope = CapEx; prelims/site admin = OpEx',
      'MRS cost code type mapping to Xero chart of accounts: L=Labour Revenue, M=Materials Revenue, E=Equipment Revenue, C=Combined Revenue',
    ],
    capabilities: 'Job cost performance, Xero integration mapping, GST treatment advice, STP Phase 2 obligations, AASB 15 revenue recognition. ' +
      'Cost code type (L/M/E/C) to Xero chart-of-accounts mapping: automates account allocation on claim push, eliminating manual journal entry. ' +
      'CapEx/OpEx classification validation on progress claim Xero push per TMR Annexure N.',
    description: 'Analyses job cost performance and validates Xero integration mappings. ' +
      'When a progress claim is pushed to Xero, maps each line item to the correct Xero account by cost code type (L→Labour Revenue, M→Materials Revenue, E→Equipment Revenue) ' +
      'and classifies CapEx vs OpEx per TMR Annexure N — eliminating manual re-allocation in Xero.',
  },
  {
    agentId: 'A05', code: 'procurement', name: 'Supplier Intelligence Agent', shortName: 'Supplier Intel',
    aqfLevel: 'AQF 7', wave: 1, status: 'ACTIVE', autonomyLevel: 1,
    primaryModules: ['pos','rfq','suppliers','catalogue','estimating'],
    triggerEvents: ['bom_line_added','task_scheduled','rfq_evaluation_closed','po_draft_created'],
    knowledgeVersion: 'PROC-v2.0.0', knowledgeCurrency: 94, nextReviewDate: '2026-12-01',
    primaryKnowledge: [
      'TMR MRTS technical specification series (approved materials)',
      'AS/NZS 4191 Traffic Signal Equipment (ITS scope)',
      'MUTCD Part 3 — approved traffic control devices',
      'Queensland Government procurement policy',
      'AS 4000-1997 — subcontractor back-to-back terms',
    ],
    capabilities: 'Product spec compliance check (MRTS + client approved products list), supplier lead time vs. task schedule conflict detection, preferred supplier ranking by category (price + compliance + delivery performance), BOM compliance flagging, real-time pricing intelligence',
    description: 'Monitors the supplier catalogue and BOM at three trigger points: when a material is added to the BOM (spec compliance + pricing), when a task is scheduled (lead time warning), and when browsing suppliers (preferred supplier ranking). Surfaces procurement intelligence so the PM and Procurement Officer make informed decisions — the agent never places orders autonomously.',
  },
  {
    agentId: 'A03', code: 'electrical-tradesman', name: 'Electrical Tradesman Agent', shortName: 'Elec. Tradesman',
    aqfLevel: 'AQF 3–5', wave: 5, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['works-instr','mobile','itp'],
    triggerEvents: ['work_instruction_assigned','task_photo_submitted'],
    knowledgeVersion: 'ET-v1.0.0', knowledgeCurrency: 88, nextReviewDate: '2027-07-01',
    primaryKnowledge: [
      'Electrical Safety Act 2002 (Qld) — licensing requirements',
      'AS/NZS 3000:2018 Part 1 — testing and commissioning',
      'Energex Network Connection Manual',
    ],
    capabilities: 'Field work instruction compliance, LOTO procedure validation, task photo QA, PTW pre-check',
    description: 'Validates field work instruction completeness, checks lockout/tagout procedures, and reviews task photos for safety compliance.',
  },
  {
    agentId: 'A10', code: 'environmental-officer', name: 'Environmental Officer Agent', shortName: 'Environmental',
    aqfLevel: 'AQF 7', wave: 5, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['environmental','site-diary','incidents'],
    triggerEvents: ['environmental_incident','spill_register_entry'],
    knowledgeVersion: 'ENV-v1.0.0', knowledgeCurrency: 87, nextReviewDate: '2027-07-01',
    primaryKnowledge: [
      'Environmental Protection Act 1994 (Qld)',
      'Nature Conservation Act 1992 (Qld)',
      'Construction Environmental Management Plan (CEMP) framework',
    ],
    capabilities: 'EPA notification obligations, spill/pollution event classification, CEMP compliance, erosion control inspection',
    description: 'Classifies environmental incidents, identifies EPA notification obligations, validates CEMP compliance, and reviews erosion and sediment control.',
  },
  {
    agentId: 'A12', code: 'estimating-qs', name: 'Estimating & QS Agent', shortName: 'QS Agent',
    aqfLevel: 'AQF 7', wave: 5, status: 'PLANNED', autonomyLevel: 1,
    primaryModules: ['estimating','rfq','catalogue','cost-codes'],
    triggerEvents: ['new_tender_added','boq_rate_outlier','boq_line_added','cost_code_library_imported','tender_submitted'],
    knowledgeVersion: 'QS-v1.1.0', knowledgeCurrency: 86, nextReviewDate: '2027-07-01',
    primaryKnowledge: [
      'AIQS Australian Construction Cost Guide',
      'TMR PCEM 9th Edition — Annexure C MRS Work Breakdown Structure for cost code assignment',
      'TMR PCEM Annexure N — CapEx/OpEx classification for transport infrastructure (ITS = CapEx)',
      'MRS91/92/93/94/204/234/256 item structure for ITS and electrical scope',
      'ATO building and construction tax guidance',
    ],
    capabilities: 'Quantity takeoff validation, market rate benchmarking, BOQ risk identification, oncost loading review. Client cost code pre-fill from MRS library (HIGH auto-assign, MEDIUM top-3, LOW flag). BOQ completeness check before tender submission — flags uncoded lines as submission risk.',
    description: 'Validates quantity takeoffs, benchmarks rates, and identifies BOQ risk items. Pre-fills MRS cost codes on BOQ lines from the client library and flags uncoded lines before tender submission. An uncoded TMR BOQ line will fail preliminary evaluation.',
  },
]

// ── Helpers ───────────────────────────────────────────────────────

export function getAgent(code: string): AgentProfile | undefined {
  return ALL_AGENTS.find(a => a.code === code)
}

export function getActiveAgents(): AgentProfile[] {
  return ALL_AGENTS.filter(a => a.status === 'ACTIVE')
}

export function getAgentsByWave(wave: AgentWave): AgentProfile[] {
  return ALL_AGENTS.filter(a => a.wave === wave)
}

// ── Mock outputs ──────────────────────────────────────────────────
// Realistic outputs given current mock platform data.
// These display until real API calls are wired.

export const MOCK_AGENT_OUTPUTS: Record<string, AgentOutput> = {
  'whs-officer': {
    urgent: [
      {
        id: 'u1',
        title: 'SWMS-009 missing fall protection controls',
        detail: 'SWMS for rooftop cable tray installation lists fall risk >2 m but has no fall protection controls (harness, edge protection, or exclusion zone). r.291(b) HRCW trigger active.',
        action: 'Add fall protection controls before work commences',
        clause: 'WHS Regulation 2011 (Qld) r.291(b)',
        ref: 'SWMS-009',
      },
    ],
    warning: [
      {
        id: 'w1',
        title: 'PTW-014 confined space controls incomplete',
        detail: 'Pit entry permit does not include atmospheric testing requirement, standby person, or rescue plan. COP Confined Spaces requirements not addressed.',
        action: 'Update PTW with confined space risk controls',
        clause: 'WHS Regulation 2011 (Qld) r.66',
        ref: 'PTW-014',
      },
      {
        id: 'w2',
        title: 'INC-003 may require WorkSafe notification',
        detail: 'Incident describes worker loss of consciousness. If electrical contact confirmed, this is a serious injury under WHS Act s.36 requiring immediate WorkSafe Qld notification.',
        action: 'Confirm mechanism of injury and notify WorkSafe if electrical',
        clause: 'WHS Act 2011 (Qld) s.36',
        ref: 'INC-003',
      },
    ],
    advisory: [
      {
        id: 'a1',
        title: 'HV proximity SWMS missing arc flash PPE category',
        detail: '3 SWMS this week include HV cable proximity work. None specify arc flash PPE category or minimum approach distances per ESA regulations.',
        action: 'Update PPE sections with arc flash category and approach distances',
        clause: 'Electrical Safety Act 2002 (Qld) s.33',
        ref: 'SWMS-007, SWMS-008',
      },
    ],
    summary: 'One urgent SWMS gap requires action before site work today. Two warnings require resolution within 24 hours. Arc flash PPE documentation needs updating across 3 active SWMS.',
  },

  'qa-specialist': {
    urgent: [],
    warning: [
      {
        id: 'w1',
        title: 'ITP-012 missing concrete curing hold point',
        detail: 'ITP for culvert headwall pour includes batching HP and placement HP but no curing period WP. MRTS73 Cl.7.4 requires 7-day minimum moist curing verification.',
        action: 'Add curing witness point to ITP-012 before pour commences',
        clause: 'MRTS73 Cl.7.4 — Curing requirements',
        ref: 'ITP-012',
      },
    ],
    advisory: [
      {
        id: 'a1',
        title: 'DWG-J1-C-004 IFC triggers 3 new hold points',
        detail: 'Drawing issued for construction — stormwater drainage. MRTS11 applies: bedding material HP, pipe jointing WP, and backfill compaction HP at 95% modified compaction required.',
        action: 'Create 3 hold points in ITP register for DWG-J1-C-004',
        clause: 'MRTS11 Cl.6 — Drainage construction',
        ref: 'DWG-J1-C-004',
      },
      {
        id: 'a2',
        title: 'J1 handover has 2 outstanding items',
        detail: 'As-built drawings not yet uploaded and NCR-004 and NCR-007 remain open. Both must be resolved before practical completion certificate can be issued.',
        action: 'Close NCR-004 and NCR-007, upload as-built drawings',
        ref: 'J1-Handover',
      },
    ],
    summary: 'One ITP gap on concrete pour requires correction before work proceeds. One new IFC drawing requires 3 hold points to be raised. Handover is on track with 2 outstanding actions.',
  },

  'contracts-admin': {
    urgent: [
      {
        id: 'u1',
        title: 'VR-002 notice expires in 3 days',
        detail: 'Variation VR-002 (changed site conditions — rock excavation) notice due 24 Jun 2026. AS4000 Cl.41.1 14-calendar-day time bar. Notice not yet issued.',
        action: 'Issue variation notice today — draft notice prepared',
        clause: 'AS 4000-1997 Cl.41.1',
        ref: 'VR-002',
      },
    ],
    warning: [
      {
        id: 'w1',
        title: 'PC-006 — no payment schedule received',
        detail: 'Progress claim submitted 28 May 2026. Superintendent has not issued payment schedule. 10 business days elapsed of 15-day BIF Act obligation.',
        action: 'Issue reminder to superintendent — 5 business days remaining',
        clause: 'BIF Act 2017 (Qld) s.69',
        ref: 'PC-006',
      },
      {
        id: 'w2',
        title: 'con2 performance bond expires in 62 days',
        detail: 'J2 — Gympie Road ITS Upgrade performance bond ($52,000) expires 28 Aug 2026. AMBER threshold reached. Renewal required before expiry.',
        action: 'Initiate bond renewal with insurer this week',
        ref: 'con2',
      },
    ],
    advisory: [
      {
        id: 'a1',
        title: 'J3 civil budget utilisation at 78%',
        detail: 'Committed PO spend against Civil cost code is $847,000 of $1,085,000 budget (78%). One further PO approval will trigger the 80% WARN threshold.',
        action: 'Review remaining civil scope before issuing further POs',
        ref: 'J3',
      },
    ],
    summary: 'One time-critical variation notice requires action today to avoid time-bar. Payment schedule monitoring on PC-006 requires attention within 5 business days. Bond renewal for J2 should be initiated this week.',
  },

  'procurement': {
    urgent: [
      {
        id: 'p-u1',
        title: 'BOM line: Signal cable CU 6mm² — non-compliant product',
        detail: 'Cable specified as PVC sheath (grey) does not meet TMR MRTS52 Cl.4.3 which requires orange sheath for buried signal cable. Current supplier quote will fail inspection.',
        action: 'Replace with AS/NZS 5000.1 rated orange-sheath cable — Rexel stock code RX-SIG6O confirmed compliant',
        clause: 'MRTS52 Cl.4.3 — Buried cable sheath colour',
        ref: 'BOM-TP001-C014',
      },
      {
        id: 'p-u2',
        title: 'Traffic cone order — lead time conflict: task TP003-TC-04 starts in 3 days',
        detail: 'Traffic cone order has 7-day standard lead time. Task scheduled to start in 3 days. Gap: 4 days. Site stoppage risk.',
        action: 'Switch to express delivery (2-day) or draw from TP-2026-001 existing stock',
        ref: 'TP003-TASK-TC04',
      },
    ],
    warning: [
      {
        id: 'p-w1',
        title: 'Signal head LED price increased 8% since estimate',
        detail: 'Rexel account price is now $342 ex GST vs estimate rate $316. Variance $26/unit × 48 units = $1,248 potential cost overrun on TP-2026-001.',
        action: 'Review unit rate with PM and update live budget, or seek competitive quote from Anixter',
        ref: 'BOM-TP001-SH',
      },
      {
        id: 'p-w2',
        title: 'Preferred supplier qualification expiring — Elecspec Technologies',
        detail: 'Elecspec Technologies public liability insurance expires in 28 days. Primary supplier for conduit fittings on J3 and J4.',
        action: 'Request updated insurance certificate from Elecspec or pause orders until renewed',
        ref: 'sup003',
      },
    ],
    advisory: [
      {
        id: 'p-a1',
        title: 'Rexel on-time delivery rate declining — 88% Q2 vs 94% Q1',
        detail: 'Still above 80% threshold but trending towards AMBER. Consider diversifying to Anixter for high-value orders on upcoming J5 mobilisation.',
        action: 'Raise delivery performance at next Rexel account review',
        ref: 'sup001',
      },
    ],
    summary: 'Two urgent actions: non-compliant cable product in BOM requires replacement before PO is raised; traffic cone lead time will cause site stoppage if not resolved today. One price variance may impact J1 budget.',
  },
}

// ── Wave roadmap ──────────────────────────────────────────────────

export const WAVE_META: Record<AgentWave, {
  label: string; target: string
  dotColor: string; bgColor: string; textColor: string; borderColor: string
}> = {
  1: { label: 'Wave 1', target: 'Q3 2026 — Active',  dotColor: 'bg-green-500', bgColor: 'bg-green-50',  textColor: 'text-green-700', borderColor: 'border-green-200' },
  2: { label: 'Wave 2', target: 'Q4 2026 — Planned', dotColor: 'bg-blue-500',  bgColor: 'bg-blue-50',   textColor: 'text-blue-700',  borderColor: 'border-blue-200'  },
  3: { label: 'Wave 3', target: 'Q1 2027 — Planned', dotColor: 'bg-gray-400',  bgColor: 'bg-gray-50',   textColor: 'text-gray-600',  borderColor: 'border-gray-200'  },
  4: { label: 'Wave 4', target: 'Q1 2027 — Planned', dotColor: 'bg-gray-400',  bgColor: 'bg-gray-50',   textColor: 'text-gray-600',  borderColor: 'border-gray-200'  },
  5: { label: 'Wave 5', target: 'Q2 2027 — Planned', dotColor: 'bg-gray-400',  bgColor: 'bg-gray-50',   textColor: 'text-gray-600',  borderColor: 'border-gray-200'  },
}
